"use client";

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Radio, ChevronRight } from 'lucide-react';
import { useLanguage } from '@/lib/language-context';
import { t } from '@/lib/translations';
import { useThreatFeed, ThreatFeedItem } from '@/lib/supabase/realtime-hooks';
import { useThreatToast } from '@/contexts/ThreatToastContext';

const statusColor: Record<string, string> = {
  Blocked: 'text-green-400 bg-green-500/10 border-green-500/25',
  Analyzing: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/25',
  Contained: 'text-blue-400 bg-blue-500/10 border-blue-500/25',
  Resolved: 'text-cyber-muted bg-white/5 border-cyber-border',
};

const severityBadge: Record<string, string> = {
  critical: 'cyber-badge-critical',
  high: 'cyber-badge-high',
  medium: 'cyber-badge-medium',
  low: 'cyber-badge-low',
};

function formatRelativeTime(iso: string): string {
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function ThreatFeedSkeletonRow() {
  return (
    <tr className="border-b border-cyber-border/50">
      <td className="px-4 py-3"><div className="w-20 h-3 rounded skeleton-cyber" /></td>
      <td className="px-4 py-3"><div className="w-14 h-4 rounded-full skeleton-cyber" /></td>
      <td className="px-4 py-3"><div className="w-24 h-3 rounded skeleton-cyber" /></td>
      <td className="px-4 py-3"><div className="w-32 h-3 rounded skeleton-cyber" /></td>
      <td className="px-4 py-3"><div className="w-24 h-3 rounded skeleton-cyber" /></td>
      <td className="px-4 py-3"><div className="w-20 h-3 rounded skeleton-cyber" /></td>
      <td className="px-4 py-3"><div className="w-16 h-4 rounded-md skeleton-cyber" /></td>
      <td className="px-4 py-3"><div className="w-12 h-3 rounded skeleton-cyber" /></td>
    </tr>
  );
}

function ThreatRow({ threat, isNew }: { threat: ThreatFeedItem; isNew: boolean }) {
  const [flash, setFlash] = useState(isNew);
  const [clicked, setClicked] = useState(false);

  useEffect(() => {
    if (isNew) {
      setFlash(true);
      const t = setTimeout(() => setFlash(false), 900);
      return () => clearTimeout(t);
    }
  }, [isNew]);

  const handleClick = () => {
    setClicked(true);
    setTimeout(() => setClicked(false), 400);
  };

  return (
    <tr
      onClick={handleClick}
      className={`transition-colors duration-100 group cursor-pointer threat-enter ${
        clicked ? 'threat-row-click' : flash ? 'row-flash' : 'hover:bg-white/3'
      }`}
    >
      <td className="px-4 py-3 font-mono text-cyber-blue whitespace-nowrap">{threat.threat_id}</td>
      <td className="px-4 py-3">
        <span className={severityBadge[threat.severity] || 'cyber-badge-info'}>
          {threat.severity}
        </span>
      </td>
      <td className="px-4 py-3 text-cyber-text whitespace-nowrap">{threat.threat_type}</td>
      <td className="px-4 py-3 font-mono text-cyber-muted max-w-[180px] truncate" title={threat.target}>
        {threat.target}
      </td>
      <td className="px-4 py-3 font-mono text-cyber-muted whitespace-nowrap">
        {threat.source_ip}
        <span className="ml-1.5 text-[9px] bg-white/5 border border-cyber-border rounded px-1 text-cyber-subtle">
          {threat.country_code}
        </span>
      </td>
      <td className="px-4 py-3 font-mono text-cyber-purple text-[11px]">{threat.technique}</td>
      <td className="px-4 py-3">
        <span className={`text-[10px] font-mono font-medium border rounded-md px-2 py-0.5 ${statusColor[threat.status] || ''}`}>
          {threat.status}
        </span>
      </td>
      <td className="px-4 py-3 font-mono text-cyber-muted whitespace-nowrap">
        {formatRelativeTime(threat.occurred_at)}
      </td>
    </tr>
  );
}

function RippleButton({ children, className, onClick, ariaLabel }: { children: React.ReactNode; className: string; onClick?: () => void; ariaLabel?: string }) {
  const btnRef = useRef<HTMLButtonElement>(null);

  const handleClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const btn = btnRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const ripple = document.createElement('span');
    ripple.className = 'ripple-circle';
    ripple.style.left = `${e.clientX - rect.left}px`;
    ripple.style.top = `${e.clientY - rect.top}px`;
    btn.appendChild(ripple);
    setTimeout(() => ripple.remove(), 600);
    onClick?.();
  }, [onClick]);

  return (
    <button ref={btnRef} onClick={handleClick} aria-label={ariaLabel} className={`ripple-btn ${className}`}>
      {children}
    </button>
  );
}

export default function LiveThreatFeed() {
  const { language } = useLanguage();
  const [filter, setFilter] = useState<'all' | 'critical' | 'high' | 'medium'>('all');
  const { addToast } = useThreatToast();
  const prevIdsRef = useRef<Set<string>>(new Set());
  const [newIds, setNewIds] = useState<Set<string>>(new Set());
  const initialLoadRef = useRef(true);

  const handleNewHighSeverity = useCallback(
    (item: ThreatFeedItem) => {
      // Skip during initial data hydration
      if (initialLoadRef.current) return;
      addToast({
        severity: item.severity as 'critical' | 'high' | 'medium' | 'low' | 'info',
        threat_type: item.threat_type,
        target: item.target,
        source_ip: item.source_ip,
        technique: item.technique,
        occurred_at: item.occurred_at,
      });
    },
    [addToast]
  );

  const { threats, loading, error } = useThreatFeed(handleNewHighSeverity);

  // Mark initial load done once data arrives
  useEffect(() => {
    if (!loading && initialLoadRef.current) {
      const timer = setTimeout(() => {
        initialLoadRef.current = false;
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [loading]);

  useEffect(() => {
    if (threats.length === 0) return;
    const currentIds = new Set(threats.map(t => t.id));
    const fresh = new Set<string>();
    currentIds.forEach(id => {
      if (!prevIdsRef.current.has(id)) fresh.add(id);
    });
    if (fresh.size > 0) {
      setNewIds(fresh);
      const timer = setTimeout(() => setNewIds(new Set()), 950);
      prevIdsRef.current = currentIds;
      return () => clearTimeout(timer);
    }
    prevIdsRef.current = currentIds;
  }, [threats]);

  const filtered = threats.filter((item: ThreatFeedItem) =>
    filter === 'all' || item.severity === filter
  );

  return (
    <div className="cyber-card flex flex-col overflow-hidden">
      <div className="px-5 py-4 border-b border-cyber-border flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Radio size={15} className="text-cyber-red" />
          <h3 className={`text-sm font-semibold text-white ${language === 'bn' ? 'font-bangla' : ''}`}>
            {t(language, 'live_feed')}
          </h3>
          <div className="flex items-center gap-1">
            <div className={`w-1.5 h-1.5 rounded-full ${loading ? 'skeleton-cyber' : 'bg-cyber-red live-dot'}`} />
            <span className="text-[10px] text-cyber-red font-mono">{loading ? 'LOADING' : 'LIVE'}</span>
          </div>
        </div>

        {/* Severity Filter */}
        <div className="flex items-center gap-1.5">
          {(['all', 'critical', 'high', 'medium'] as const).map((f) => (
            <RippleButton
              key={f}
              onClick={() => setFilter(f)}
              className={`px-2.5 py-1 rounded-md text-[10px] font-mono font-medium transition-all duration-150 capitalize ${
                filter === f
                  ? 'bg-cyber-blue/20 text-cyber-blue border border-cyber-blue/30' :'text-cyber-muted hover:text-cyber-text border border-transparent hover:border-cyber-border'
              }`}
              ariaLabel={`Filter by ${f}`}
            >
              {f}
            </RippleButton>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto scrollbar-cyber">
        {error ? (
          <div className="px-5 py-8 text-center">
            <p className="text-xs text-cyber-red font-mono">Failed to load threat feed</p>
          </div>
        ) : loading ? (
          <table className="w-full text-xs" role="table" aria-label="Loading threat feed" aria-busy="true">
            <thead>
              <tr className="border-b border-cyber-border" role="row">
                {['ID', 'Severity', 'Type', 'Target', 'Source', 'Technique', 'Status', 'Time'].map((col) => (
                  <th key={col} scope="col" className="px-4 py-2.5 text-left text-[10px] font-mono text-cyber-muted uppercase tracking-widest whitespace-nowrap">
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 6 }).map((_, i) => (
                <ThreatFeedSkeletonRow key={i} />
              ))}
            </tbody>
          </table>
        ) : (
          <table className="w-full text-xs" role="table" aria-label="Live threat feed">
            <thead>
              <tr className="border-b border-cyber-border" role="row">
                {['ID', 'Severity', 'Type', 'Target', 'Source', 'Technique', 'Status', 'Time'].map((col) => (
                  <th key={col} scope="col" className="px-4 py-2.5 text-left text-[10px] font-mono text-cyber-muted uppercase tracking-widest whitespace-nowrap">
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-cyber-border/50">
              {filtered.map((threat: ThreatFeedItem) => (
                <ThreatRow key={threat.id} threat={threat} isNew={newIds.has(threat.id)} />
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-6 text-center text-xs text-cyber-muted font-mono">
                    No threats matching current filter
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <div className="px-5 py-3 border-t border-cyber-border flex items-center justify-between">
        <span className="text-[11px] text-cyber-muted font-mono">
          {loading ? (
            <span className="w-28 h-3 rounded skeleton-cyber inline-block" />
          ) : (
            `Showing ${filtered.length} of ${threats.length} threats`
          )}
        </span>
        <RippleButton
          className="flex items-center gap-1.5 text-[11px] text-cyber-blue hover:text-cyber-blue/80 transition-colors font-medium"
          ariaLabel="View all threats"
        >
          {t(language, 'view_all')} <ChevronRight size={12} />
        </RippleButton>
      </div>
    </div>
  );
}